import React, { useState, useEffect, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";

import SubscriptionModal from "../../components/SubscriptionModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { createInvoiceFromPlan } from "../../services/subscription.api";
import { getCurrency } from "../../services/settings.api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Función helper para obtener el símbolo de la moneda
const getCurrencySymbol = (currency) => {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    BRL: 'R$',
    MXN: '$',
    ARS: '$',
    CLP: '$',
    COP: '$',
    PEN: 'S/'
  };
  return symbols[currency] || '$';
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  planCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  },
  planHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: theme.spacing(2),
    textAlign: "center",
  },
  planPrice: {
    display: "flex",
    justifyContent: "center",
    alignItems: "baseline",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  planFeatures: {
    flex: 1,
    padding: theme.spacing(2),
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.success.main,
    },
  },
  planActions: {
    padding: theme.spacing(2),
  },
  infoCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
}));

const SubscriptionContent = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { list: listPlans, finder: findPlan } = usePlans();

  const numberLocale = useMemo(() => {
    const lang = (i18n.language || "es").split("-")[0];
    if (lang === "en") return "en-US";
    if (lang === "pt") return "pt-BR";
    return "es-ES";
  }, [i18n.language]);

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [currentPlanName, setCurrentPlanName] = useState(
    () => i18n.t("financial.subscription.noPlanAssigned")
  );
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [currency, setCurrency] = useState("USD");

  useEffect(() => {
    loadPlans();
    loadCurrentPlan();
    loadCurrency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.company?.planId) {
      setCurrentPlanName(i18n.t("financial.subscription.noPlanAssigned"));
    }
  }, [i18n.language, user?.company?.planId]);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const plansList = await listPlans();
      setPlans(plansList);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentPlan = async () => {
    if (user?.company?.planId) {
      try {
        const plan = await findPlan(user.company.planId);
        setCurrentPlanName(plan.name);
      } catch (err) {
        console.error("Error loading current plan:", err);
        setCurrentPlanName(i18n.t("financial.subscription.noPlanAssigned"));
      }
    }
  };

  const loadCurrency = async () => {
    try {
      const { data } = await getCurrency();
      if (data?.value) {
        setCurrency(data.value);
      }
    } catch (err) {
      // Si no existe, usar USD por defecto
      setCurrency("USD");
    }
  };

  const handleSelectPlan = async (plan) => {
    setCreatingInvoice(true);
    try {
      // Crear factura desde el plan seleccionado
      const { data: invoice } = await createInvoiceFromPlan({
        planId: plan.id,
      });

      // Abrir modal de checkout con la factura creada
      setSelectedInvoice(invoice);
      setSelectedContactId(null);
      setContactModalOpen(true);
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setSelectedInvoice(null);
    setContactModalOpen(false);
    // Recargar planes después de cerrar el modal
    loadPlans();
    loadCurrentPlan();
  };

  return (
    <>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={selectedInvoice}
        contactId={selectedContactId}
      />

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Información del usuario */}
        <Card className={classes.infoCard} variant="outlined">
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="current-plan"
                  label={i18n.t("financial.subscription.currentPlanLabel")}
                  value={currentPlanName}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Planes disponibles */}
        <Typography variant="h5" gutterBottom style={{ marginTop: 24, marginBottom: 16 }}>
          {i18n.t("financial.subscription.availablePlans")}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
          {i18n.t("financial.subscription.availablePlansHint")}
        </Typography>

        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {plans.map((plan) => (
              <Grid item key={plan.id} xs={12} sm={6} md={4}>
                <Card className={classes.planCard} variant="outlined">
                  <Box className={classes.planHeader}>
                    <Typography variant="h5" component="h2">
                      {plan.name}
                    </Typography>
                  </Box>
                  <CardContent className={classes.planFeatures}>
                    <Box className={classes.planPrice}>
                      <Typography component="h2" variant="h3" color="textPrimary">
                        {getCurrencySymbol(currency)}{plan.value.toLocaleString(numberLocale, { minimumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="h6" color="textSecondary" style={{ marginLeft: 8 }}>
                        {i18n.t("financial.subscription.perMonth")}
                      </Typography>
                    </Box>
                    <Box>
                      <Box className={classes.featureItem}>
                        <CheckIcon fontSize="small" />
                        <Typography variant="body2">
                          {i18n.t("financial.subscription.users", { count: plan.users })}
                        </Typography>
                      </Box>
                      <Box className={classes.featureItem}>
                        <CheckIcon fontSize="small" />
                        <Typography variant="body2">
                          {i18n.t("financial.subscription.connections", {
                            count: plan.connections,
                          })}
                        </Typography>
                      </Box>
                      <Box className={classes.featureItem}>
                        <CheckIcon fontSize="small" />
                        <Typography variant="body2">
                          {i18n.t("financial.subscription.queues", { count: plan.queues })}
                        </Typography>
                      </Box>
                      {plan.useCampaigns && (
                        <Box className={classes.featureItem}>
                          <CheckIcon fontSize="small" />
                          <Typography variant="body2">
                            {i18n.t("financial.subscription.featureCampaigns")}
                          </Typography>
                        </Box>
                      )}
                      {plan.useSchedules && (
                        <Box className={classes.featureItem}>
                          <CheckIcon fontSize="small" />
                          <Typography variant="body2">
                            {i18n.t("financial.subscription.featureSchedules")}
                          </Typography>
                        </Box>
                      )}
                      {plan.useOpenAi && (
                        <Box className={classes.featureItem}>
                          <CheckIcon fontSize="small" />
                          <Typography variant="body2">
                            {i18n.t("financial.subscription.featureOpenAi")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                  <CardActions className={classes.planActions}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => handleSelectPlan(plan)}
                      disabled={creatingInvoice}
                    >
                      {creatingInvoice ? (
                        <>
                          <CircularProgress size={20} style={{ marginRight: 8 }} />
                          {i18n.t("financial.subscription.processing")}
                        </>
                      ) : (
                        i18n.t("financial.subscription.subscribe")
                      )}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && plans.length === 0 && (
          <Box style={{ textAlign: "center", padding: 40 }}>
            <Typography variant="h6" color="textSecondary">
              {i18n.t("financial.subscription.noPlansTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {i18n.t("financial.subscription.noPlansHint")}
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default SubscriptionContent;

